import { useTranslation } from 'core/i18n/i18n';
import { useNavigate } from 'react-router-dom';
import { PredefinedCommentFormModel } from '../settings-devices-predefined-comments-form.model';
import {
    createPredefinedComment,
    updatePredefinedComment,
} from 'modules/settings/pods/settings-devices/settings-devices-predefined-comments/service/predefined-comments.service';
import { showErrorNotification, showSuccessNotification } from 'core/utils/notification-service';
import { settingsModulePaths } from 'core/routes/routes';
import { settingsKeys } from 'core/query-client/keys/settings.keys';
import { updateQuery } from 'core/query-client/query-client';
import { GraphqlError } from 'core/utils/backend-service';
import { mapPredefinedCommentsFormErrorsFromAPI } from 'modules/settings/pods/settings-devices/settings-devices-predefined-comments/settings-devices-predefined-comments-form/settings-devices-predefined-comments-form.mapper';
import { usePredefinedCommentValidation } from 'modules/settings/pods/settings-devices/settings-devices-predefined-comments/settings-devices-predefined-comments-form/hooks/use-predefined-comment-validation.hook';
import { usePredefinedCommentFormContext } from 'modules/settings/pods/settings-devices/settings-devices-predefined-comments/settings-devices-predefined-comments-form/hooks/use-predefined-comment-form-context.hook';

export const usePredefinedCommentSaver = () => {
    const { t } = useTranslation();
    const { setApiErrors } = usePredefinedCommentValidation();
    const { predefinedCommentId } = usePredefinedCommentFormContext();
    const navigate = useNavigate();

    const handleApiError = (e: Error, fallbackKey: string) => {
        const parsedError = JSON.parse(e.message);
        if (parsedError.errors?.length > 0) {
            const apiErrors = parsedError.errors as GraphqlError[];
            setApiErrors(mapPredefinedCommentsFormErrorsFromAPI(apiErrors));
        } else {
            showErrorNotification(t(fallbackKey));
        }
    };

    const savePredefinedComment = (formData: PredefinedCommentFormModel) =>
        createPredefinedComment(formData)
            .then(() => {
                showSuccessNotification(t('settings.devices.predefined-comments.form.notification.create.success'));
                updateQuery(settingsKeys.predefinedComments());
                navigate(settingsModulePaths.predefinedComments);
            })
            .catch((e) => handleApiError(e, 'settings.devices.predefined-comments.form.notification.create.error'));

    const editPredefinedComment = (formData: PredefinedCommentFormModel) => {
        if (!predefinedCommentId) {
            return Promise.reject(new Error('editPredefinedComment requires predefinedCommentId'));
        }
        return updatePredefinedComment(predefinedCommentId, formData)
            .then(() => {
                showSuccessNotification(t('settings.devices.predefined-comments.form.notification.update.success'));
                updateQuery(settingsKeys.predefinedComments());
                navigate(settingsModulePaths.predefinedComments);
            })
            .catch((e) => handleApiError(e, 'settings.devices.predefined-comments.form.notification.update.error'));
    };
    return { savePredefinedComment, editPredefinedComment };
};
